import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { Widget } from "@src/types/dashboards";
import { postAddWidgetFromTemplateV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/post-add-widget-from-template-v2-use-case";
import { putUpdateWidgetV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/put-update-widget-v2-use-case";
import { getDashboardByIdV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/get-dashboard-by-id-v2-use-case";
import {
  commitPendingWidget,
  upsertDashboard,
} from "@src/reducers/dashboards-slice";
import { withMultipleTextValuesHeight } from "@src/components/organisms/widgets/multiple-text-values/multiple-text-values-height";
import { findFreeSlot } from "@src/components/organisms/widgets/find-free-slot";
import {
  errorSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

export interface SaveWidgetOptions {
  /** Multipart image part: a File replaces the asset, `null` keeps it,
   *  `undefined` clears it. Omitting the key is the same as passing `null`. */
  imageFile?: File | null;
  /** Runs before the request. Returning a message blocks the save and shows
   *  that message to the user, so an incomplete form never reaches the BE. */
  validate?: () => string | null;
  /** Entry count of the widget's content, for the content-sized types whose
   *  tile height is derived from it on save. */
  contentRowCount?: number;
}

export interface UseSaveWidgetResult {
  /** Persists `updated` (POST for a not-yet-saved widget, PUT otherwise),
   *  syncs the dashboard from the BE and closes the editor. */
  save: (updated: Widget, options?: SaveWidgetOptions) => Promise<void>;
  isSaving: boolean;
}

/**
 * The save flow every widget editor shares: validate, POST/PUT, re-fetch the
 * dashboard, close. Owns the in-flight lock (a fast double-click on Save
 * would otherwise fire two requests, since the button's disabled attribute
 * only lands after React commits) and the Save spinner / Cancel-disabled
 * state that goes with it.
 */
export const useSaveWidget = (
  widget: Widget | undefined,
  isPendingWidget: boolean,
  onClose: () => void,
): UseSaveWidgetResult => {
  const dispatch = useDispatch();
  const selectedDashboard = useSelector((state: RootState) =>
    state.dashboards.dashboards.find(
      (d) => d.id === state.dashboards.selectedDashboardId,
    ),
  );
  const [isSaving, setIsSaving] = useState(false);
  const saveInFlightRef = useRef(false);

  const save = async (updated: Widget, options: SaveWidgetOptions = {}) => {
    const { validate, contentRowCount = 0 } = options;
    // Read `imageFile` off the options object instead of destructuring with a
    // default: `undefined` IS the Delete intent, and a destructuring default
    // would silently rewrite it to `null` ("keep the asset"), so the delete
    // never reached the multipart writer. Only a MISSING key means no change.
    const imageFile: File | null | undefined =
      "imageFile" in options ? options.imageFile : null;
    if (saveInFlightRef.current) return;
    if (!widget || !selectedDashboard) return;
    const validationError = validate?.() ?? null;
    if (validationError) {
      warningSnackbar(validationError);
      return;
    }
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      // The widget's id comes from the URL, never the body, and
      // `presentation.image_url` is obsolete — the canonical location is the
      // top-level `image_url`. Both are `extra_forbidden` on the BE models.
      const { id: _stripId, presentation: presRaw, ...rest } = updated;
      const presentation = (
        presRaw
          ? Object.fromEntries(
              Object.entries(presRaw).filter(([k]) => k !== "image_url"),
            )
          : {}
      ) as typeof updated.presentation;
      const widgetSpec = { ...rest, presentation };
      let positioning = withMultipleTextValuesHeight(
        widget.type,
        selectedDashboard.positioning[widget.id],
        contentRowCount,
      );
      if (positioning && widget.type === "multiple-text-values") {
        const others = Object.entries(selectedDashboard.positioning)
          .filter(([id]) => id !== widget.id)
          .map(([, other]) => other);
        const { x, y, w, h } = positioning;
        const overlapsOther = others.some(
          (o) => x < o.x + o.w && x + w > o.x && y < o.y + o.h && y + h > o.y,
        );
        if (overlapsOther) {
          const columns = selectedDashboard.columnCount || 24;
          positioning = { ...findFreeSlot(others, columns, w, h), w, h };
        }
      }

      if (isPendingWidget) {
        // One-call commit: the BE accepts the full widget body, assigns the
        // real id and returns the committed widget — no follow-up PUT and no
        // compensation DELETE. Either it is created or it is not.
        const tempId = widget.id;
        const added = await postAddWidgetFromTemplateV2UseCase(
          selectedDashboard.id,
          widgetSpec,
          positioning,
          imageFile,
        );
        if (!added.success || !added.data) {
          console.error("Failed to add widget:", added.error);
          errorSnackbar(
            `Couldn't add widget: ${added.error ?? "unknown error"}`,
          );
          // Keep the drawer open and the pending tile in place: the user can
          // fix the input that triggered the 422 and retry Save without
          // losing the rest of their edits.
          setIsSaving(false);
          return;
        }

        dispatch(
          commitPendingWidget({
            dashboardId: selectedDashboard.id,
            tempId,
            realWidget: added.data,
          }),
        );
      } else {
        const result = await putUpdateWidgetV2UseCase(
          selectedDashboard.id,
          widget.id,
          widgetSpec,
          positioning,
          imageFile,
        );
        if (!result.success) {
          console.error("Failed to save widget:", result.error);
          errorSnackbar(
            `Couldn't save widget: ${result.error ?? "validation failed"}`,
          );
          setIsSaving(false);
          return;
        }
      }

      const refreshed = await getDashboardByIdV2UseCase(selectedDashboard.id);
      if (refreshed.success && refreshed.data) {
        dispatch(upsertDashboard(refreshed.data));
      }
      // Closing unmounts the editor, which drops this state anyway; clearing it
      // here keeps the save flow correct if a caller ever keeps it mounted.
      setIsSaving(false);
      onClose();
    } catch (error) {
      console.error("Save widget failed:", error);
      setIsSaving(false);
    } finally {
      // Released on every exit path — this is what actually stops a
      // double-click from firing a second request.
      saveInFlightRef.current = false;
    }
  };

  return { save, isSaving };
};
